import { useState } from "react";
import { motion } from "framer-motion";
import { fadeUp, waLink } from "@/lib/site-utils";
import { GALLERY } from "@/data/site";
import { ArrowUpRight, X, Maximize2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export const Gallery = () => {
  const [active, setActive] = useState(null);
  return (
    <section id="gallery" data-testid="gallery-section" className="relative border-t border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <span className="font-plex text-xs tracking-[0.25em] uppercase text-terra">यात्रा की झलकियाँ / GALLERY</span>
            <h2 className="mt-4 font-khand font-700 uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl text-[#f5f5f0]">
              खुश ग्राहक, यादगार सफर
            </h2>
          </div>
          <p className="max-w-sm font-hind text-neutral-400">
            शादी से लेकर कश्मीर टूर तक — SS Student Travels के साथ हर सफर खास बनता है।
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[240px] gap-4 md:gap-6">
          {GALLERY.map((g, i) => (
            <motion.div
              key={g.id}
              variants={fadeUp}
              custom={i % 3}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              data-testid={`gallery-card-${g.id}`}
              onClick={() => setActive(g)}
              className={`group relative overflow-hidden border border-white/10 cursor-pointer ${g.span}`}
            >
              <img
                src={g.img}
                alt={g.en}
                loading="lazy"
                className={`absolute inset-0 h-full w-full object-cover ${g.pos || "object-center"} group-hover:scale-105 transition-transform duration-700`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/25 to-transparent" />
              <div className="absolute top-4 right-4 grid place-items-center h-10 w-10 bg-black/40 backdrop-blur border border-white/20 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Maximize2 size={16} />
              </div>
              <div className="absolute bottom-0 inset-x-0 p-6">
                <p className="font-plex text-[10px] tracking-[0.2em] uppercase text-terra">{g.en}</p>
                <h3 className="font-khand font-700 text-2xl md:text-3xl leading-none text-[#f5f5f0] mt-1">{g.hi}</h3>
              </div>
            </motion.div>
          ))}
        </div>

        <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
          <DialogContent
            data-testid="gallery-lightbox"
            className="max-w-4xl w-[92vw] bg-stone border-white/10 p-0 overflow-hidden [&>button]:hidden"
          >
            {active && (
              <div className="relative">
                <button
                  data-testid="lightbox-close"
                  onClick={() => setActive(null)}
                  className="absolute top-3 right-3 z-10 grid place-items-center h-10 w-10 bg-black/60 backdrop-blur border border-white/20 text-white hover:bg-jcb hover:text-black transition-colors duration-200"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
                <img src={active.img} alt={active.en} className="w-full max-h-[75vh] object-contain bg-black" />
                <div className="p-5 border-t border-white/10">
                  <p className="font-plex text-[10px] tracking-[0.2em] uppercase text-terra">{active.en}</p>
                  <h3 className="font-khand font-700 text-2xl text-[#f5f5f0]">{active.hi}</h3>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-12">
          <a
            data-testid="gallery-book-btn"
            href={waLink("नमस्ते SS Student Travels! मुझे टूर/यात्रा बुकिंग की जानकारी चाहिए।")}
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-3 bg-terra text-black px-7 py-3.5 font-khand font-600 text-lg tracking-wide hover:bg-white transition-colors duration-200"
          >
            अपनी यात्रा बुक करें / Plan Your Trip
            <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
